import React, {useEffect, useState} from 'react';
import {lessonEditorText} from '../../../lessonEditorText';
import {OpenEndedQuestionContent} from '../../../../../data/models/LessonContent/LessonContentBase';
import EditorOptionsFooter from '../../../../../uiToolkit/Editor/EditorOptionsFooter';
import ReplaceNode from '../../ReplaceNode';
import Dropdown from '../../../../../uiToolkit/Inputs/Dropdown';
import {GradeWeights} from '../../../../../data/models/LessonContent/GradeWeights';
import Checkbox from '../../../../../uiToolkit/Inputs/Checkbox';
import NumbersInput from '../../../../../uiToolkit/Inputs/NumbersInput';
import FileExtensionsInput from '../../../../../uiToolkit/Inputs/FileExtensionsInput';
import TextAreaInput from '../../../../../uiToolkit/Inputs/TextAreaInput';

interface Props {
  entityToEdit: OpenEndedQuestionContent;
  onSave: (
    gradeWeight: number,
    textAreaAnswerEnabled: boolean,
    textAreaAnswerPrompt: string,
    textAreaAnswerMaxCharacterCount: number,
    attachmentAnswerEnabled: boolean,
    attachmentAnswerPrompt: string,
    attachmentAnswerFileTypes: string[],
    attachmentAnswerMaxFileKb: number,
  ) => void;
  isLoading: boolean;
  onDelete: () => void;
  isPublished?: boolean;
  onSoftDelete?: () => void;
}

const OpenEndedQuestionOptionsUI = ({
  entityToEdit,
  onSave,
  isLoading,
  onDelete,
  isPublished,
  onSoftDelete,
}: Props) => {
  const [gradeWeight, setGradeWeight] = useState(1);
  const [textAreaAnswerEnabled, setTextAreaAnswerEnabled] = useState(false);
  const [textAreaAnswerPrompt, setTextAreaAnswerPrompt] = useState('');
  const [textAreaAnswerMaxCharacterCount, setTextAreaAnswerMaxCharacterCount] =
    useState(0);
  const [attachmentAnswerEnabled, setAttachmentAnswerEnabled] = useState(false);
  const [attachmentAnswerPrompt, setAttachmentAnswerPrompt] = useState('');
  const [attachmentAnswerFileTypes, setAttachmentAnswerFileTypes] = useState<
    string[]
  >([]);
  const [attachmentAnswerMaxFileKb, setAttachmentAnswerMaxFileKb] = useState(0);

  if (!entityToEdit) {
    throw new Error('entityToEdit is required!');
  }

  useEffect(() => {
    setGradeWeight(entityToEdit.gradeWeight);
    setTextAreaAnswerEnabled(entityToEdit.textAreaAnswerEnabled);
    setTextAreaAnswerPrompt(entityToEdit.textAreaAnswerPrompt);
    setTextAreaAnswerMaxCharacterCount(
      entityToEdit.textAreaAnswerMaxCharacterCount,
    );
    setAttachmentAnswerEnabled(entityToEdit.attachmentAnswerEnabled);
    setAttachmentAnswerPrompt(entityToEdit.attachmentAnswerPrompt);
    setAttachmentAnswerFileTypes(entityToEdit.attachmentAnswerFileTypes);
    setAttachmentAnswerMaxFileKb(entityToEdit.attachmentAnswerMaxFileKb);
  }, [entityToEdit]);

  const isFormValid =
    (textAreaAnswerEnabled || attachmentAnswerEnabled) &&
    ((textAreaAnswerEnabled && textAreaAnswerPrompt.length > 0) ||
      !textAreaAnswerEnabled) &&
    ((attachmentAnswerEnabled &&
      attachmentAnswerPrompt.length > 0 &&
      attachmentAnswerMaxFileKb > 0 &&
      attachmentAnswerMaxFileKb < 1024 * 1024 * 10) ||
      !attachmentAnswerEnabled);

  const saveChanges = () => {
    if (isFormValid) {
      onSave(
        gradeWeight,
        textAreaAnswerEnabled,
        textAreaAnswerPrompt,
        textAreaAnswerMaxCharacterCount,
        attachmentAnswerEnabled,
        attachmentAnswerPrompt,
        attachmentAnswerFileTypes,
        attachmentAnswerMaxFileKb,
      );
    }
  };

  return (
    <div>
      <Checkbox
        text={lessonEditorText.gradeWeightEnabled}
        onCheck={() =>
          setGradeWeight(
            gradeWeight == 0
              ? entityToEdit.gradeWeight > 0
                ? entityToEdit.gradeWeight
                : 1
              : 0,
          )
        }
        checked={gradeWeight != 0}
      />
      <div>
        <Dropdown
          disabled={gradeWeight == 0}
          options={GradeWeights}
          setSelectedOption={val => setGradeWeight(Number.parseInt(val))}
          selectedOption={gradeWeight.toString()}
          heading={lessonEditorText.gradeWeight}
          type="box"
        />
      </div>
      <Checkbox
        text={lessonEditorText.textAreaAnswerEnabled}
        onCheck={() => setTextAreaAnswerEnabled(!textAreaAnswerEnabled)}
        checked={textAreaAnswerEnabled}
      />
      <div>
        <TextAreaInput
          disabled={!textAreaAnswerEnabled}
          value={textAreaAnswerPrompt}
          onChange={setTextAreaAnswerPrompt}
          placeholder={lessonEditorText.textAreaAnswerPrompt}
          fieldError={
            textAreaAnswerEnabled && textAreaAnswerPrompt.trim() === ''
          }
          maxCharacters={200}
          uiType="box"
        />
        <NumbersInput
          disabled={!textAreaAnswerEnabled}
          heading={lessonEditorText.maxCharacterCount}
          value={textAreaAnswerMaxCharacterCount}
          setValue={val => setTextAreaAnswerMaxCharacterCount(val ?? 0)}
          maxValue={750}
          minValue={1}
          uiType="box"
        />
      </div>
      <Checkbox
        text={lessonEditorText.attachmentAnswerEnabled}
        onCheck={() => setAttachmentAnswerEnabled(!attachmentAnswerEnabled)}
        checked={attachmentAnswerEnabled}
      />
      <div>
        <TextAreaInput
          disabled={!attachmentAnswerEnabled}
          value={attachmentAnswerPrompt}
          onChange={setAttachmentAnswerPrompt}
          placeholder={lessonEditorText.attachmentAnswerPrompt}
          fieldError={
            attachmentAnswerEnabled && attachmentAnswerPrompt.trim() === ''
          }
          maxCharacters={200}
          uiType="box"
        />
        <FileExtensionsInput
          disabled={!attachmentAnswerEnabled}
          value={attachmentAnswerFileTypes}
          heading={lessonEditorText.attachmentAnswerFileTypes}
          onChange={setAttachmentAnswerFileTypes}
          maxCharacters={100}
          uiType="box"
        />
        <NumbersInput
          disabled={!attachmentAnswerEnabled}
          heading={lessonEditorText.maxFileSizeKb}
          value={attachmentAnswerMaxFileKb}
          setValue={val => setAttachmentAnswerMaxFileKb(val ?? 0)}
          maxValue={1024 * 100} // 100 MB
          uiType="box"
        />
      </div>
      <ReplaceNode currentNode={entityToEdit} />
      <EditorOptionsFooter
        onSave={saveChanges}
        saveEnabled={isFormValid}
        onDelete={onDelete}
        isLoading={isLoading}
        deleteDisabled={isPublished}
        onSoftDelete={onSoftDelete}
      />
    </div>
  );
};

export default OpenEndedQuestionOptionsUI;
